"use client"

import type { ReactNode } from "react"

import { useIsMobile } from "@/hooks/use-mobile"
import { usePresence } from "@/hooks/use-presence"
import { cn } from "@/lib/utils"

const DURATION_MS = 220

/**
 * The sheet the candidate record — and anything else that slides over the ATS
 * column — lives in.
 *
 * It owns three things that were previously spread around and getting them
 * wrong:
 *
 *   - **One mount.** Loading, error and loaded states render as *children* of a
 *     single sheet. They used to be sibling branches that each rendered their
 *     own frame, so React unmounted one and mounted the other when the fetch
 *     resolved and the entrance animation replayed — the record appeared,
 *     vanished and reappeared.
 *   - **An exit.** `usePresence` holds the mount open while the closing
 *     animation runs, instead of the panel blinking out of existence the moment
 *     its id clears.
 *   - **Where it is positioned.** `absolute`, not `fixed`: canvas 6a opens the
 *     record over the ATS column only, with the conversation rail and thread
 *     still visible beside it. The positioning context is the ATS panel in
 *     `robin-workspace.tsx`. On a phone there is nothing to sit beside, so it
 *     takes the screen.
 */
export function OverlaySheet({
  open,
  onClose,
  children,
  label,
}: {
  open: boolean
  onClose: () => void
  children: ReactNode
  label?: string
}) {
  const isMobile = useIsMobile()
  const { mounted, state } = usePresence(open, DURATION_MS)

  if (!mounted) return null

  if (isMobile) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-state={state}
        className={cn(
          // Tailwind v4 compiles translate utilities to the `translate`
          // property, not `transform`, so `transition-transform` animates
          // nothing at all here.
          "fixed inset-0 z-50 bg-background transition-[translate,opacity] duration-200 ease-out",
          state === "open" ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0",
        )}
      >
        {children}
      </div>
    )
  }

  return (
    <>
      {/* Scrim — the list stays legible behind the record. */}
      <div
        data-state={state}
        onClick={onClose}
        aria-hidden
        className={cn(
          "absolute inset-0 z-40 bg-scrim transition-opacity ease-out",
          state === "open" ? "opacity-100 duration-200" : "opacity-0 duration-150",
        )}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-state={state}
        className={cn(
          "absolute inset-y-0 right-0 z-50 w-full max-w-[980px] border-l bg-background",
          // `translate`, not `transform` — see the note on the mobile branch.
          "shadow-[-24px_0_60px_-20px_rgb(0_0_0/0.55)] transition-[translate] ease-out",
          state === "open"
            ? "translate-x-0 duration-300"
            : "translate-x-full duration-200",
        )}
      >
        {children}
      </div>
    </>
  )
}
